import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Camera, Upload, CheckCircle, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UNIT_TYPES = ['Hours', 'Tons', 'Loads'];

export default function LogEntry() {
  const { customers, addLog, user, isAdmin } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNoCustomerModal, setShowNoCustomerModal] = useState(isAdmin && customers.length === 0);
  const [form, setForm] = useState({
    driverName: user?.displayName || '',
    customerId: '',
    jobNumber: '',
    unitType: 'Hours',
    quantity: '',
    workDescription: '',
    truckNumber: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const getQuantityLabel = () => {
    if (form.unitType === 'Hours') return 'Hours Worked *';
    if (form.unitType === 'Tons') return 'Tons *';
    return 'Number of Loads *';
  };

  const getQuantityPlaceholder = () => {
    if (form.unitType === 'Hours') return 'e.g. 7.5';
    if (form.unitType === 'Tons') return 'e.g. 22.5';
    return 'e.g. 4';
  };

  const handleSubmit = async () => {
    if (!form.customerId || !form.quantity || !form.driverName) {
      alert('Please fill in Driver Name, Customer, and ' + form.unitType + '.');
      return;
    }
    setLoading(true);
    const customer = customers.find(c => c.id === form.customerId);
    await addLog({ ...form, customerName: customer?.name, photoPreview, hours: form.unitType === 'Hours' ? form.quantity : null });
    setSubmitted(true);
    setLoading(false);
  };

  const reset = () => {
    setForm({ driverName: user?.displayName || '', customerId: '', jobNumber: '', unitType: 'Hours', quantity: '', workDescription: '', truckNumber: '', notes: '', date: new Date().toISOString().split('T')[0] });
    setPhotoPreview(null);
    setSubmitted(false);
  };

  if (submitted) return (
    <div className="page" style={{ paddingTop: 60, textAlign: 'center' }}>
      <CheckCircle size={56} color="var(--success)" style={{ marginBottom: 16 }} />
      <h2 style={{ fontSize: 28, marginBottom: 8 }}>Log Submitted</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32 }}>Your work log has been recorded successfully.</p>
      <button className="btn-primary" onClick={reset}>Submit Another Log</button>
    </div>
  );

  return (
    <div className="page">

      {/* No Customer Modal — admin only */}
      {showNoCustomerModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: 32,
            maxWidth: 380,
            width: '100%',
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <AlertCircle size={44} color="var(--accent)" style={{ marginBottom: 12 }} />
              <h2 style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
                No Customers Added Yet
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Before you can log work, you need to add at least one customer and their rate in the <strong style={{ color: 'var(--text)' }}>Admin Dashboard → Customers tab</strong>.
              </p>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, marginTop: 10 }}>
                Once customers are added, drivers will be able to select them when submitting logs.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                className="btn-primary"
                onClick={() => navigate('/admin')}
                style={{ width: '100%', padding: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                Go to Admin Dashboard
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowNoCustomerModal(false)}
                style={{ width: '100%', padding: '11px' }}
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, marginBottom: 4 }}>Work Log Entry</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Fill out your daily work slip details below.</p>
      </div>

      {/* Photo Upload */}
      <div className="card" style={{ marginBottom: 20 }}>
        <label style={{ marginBottom: 12 }}>Signed Work Slip Photo (Optional)</label>
        {photoPreview ? (
          <div style={{ position: 'relative' }}>
            <img src={photoPreview} alt="slip" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 'var(--radius)' }} />
            <button type="button" onClick={() => setPhotoPreview(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div onClick={() => fileRef.current.click()}
            style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius)', padding: '32px 20px', textAlign: 'center', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <Camera size={28} color="var(--text-dim)" style={{ marginBottom: 8 }} />
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Tap to photograph your signed work slip</p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
      </div>

      <div className="card">
        <div className="field">
          <label>Driver Name *</label>
          <input value={form.driverName} onChange={set('driverName')} placeholder="Your full name" />
        </div>

        <div className="grid-2">
          <div className="field">
            <label>Date *</label>
            <input type="date" value={form.date} onChange={set('date')} />
          </div>
          <div className="field">
            <label>Truck Number</label>
            <input value={form.truckNumber} onChange={set('truckNumber')} placeholder="e.g. T-14" />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label>Customer *</label>
            <select value={form.customerId} onChange={set('customerId')}>
              <option value="">— Select Customer —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Job Number</label>
            <input value={form.jobNumber} onChange={set('jobNumber')} placeholder="e.g. JOB-1042" />
          </div>
        </div>

        {/* Unit Type Selector */}
        <div className="field">
          <label>Pay Type *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {UNIT_TYPES.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setForm(f => ({ ...f, unitType: type }))}
                style={{
                  flex: 1, padding: '10px',
                  border: form.unitType === type ? '2px solid var(--accent)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  background: form.unitType === type ? 'var(--accent-dim)' : 'var(--bg3)',
                  color: form.unitType === type ? 'var(--accent)' : 'var(--text-muted)',
                  fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 13,
                  letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>{getQuantityLabel()}</label>
          <input type="number" step={form.unitType === 'Loads' ? '1' : '0.5'} min="0" value={form.quantity} onChange={set('quantity')} placeholder={getQuantityPlaceholder()} />
        </div>

        <div className="field">
          <label>Type of Work / Description *</label>
          <textarea value={form.workDescription} onChange={set('workDescription')} placeholder="Describe the work performed..." rows={3} />
        </div>

        <div className="field">
          <label>Additional Notes</label>
          <textarea value={form.notes} onChange={set('notes')} placeholder="Any additional notes..." rows={2} />
        </div>

        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={loading}
          style={{ width: '100%', padding: '12px 20px', fontSize: 15, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Upload size={15} />
          {loading ? 'Submitting...' : 'Submit Work Log'}
        </button>
      </div>
    </div>
  );
}
